import { useState, useMemo } from 'react';
import type { IntegrationFilterValue } from '@/mocks/saIntegrations';
import { saIntegrations } from '@/mocks/saIntegrations';
import SASidebar from '@/pages/sa/dashboard/components/SASidebar';
import IntegrationCard from './components/IntegrationCard';
import IntegrationListItem from './components/IntegrationListItem';
import IntegrationFilters from './components/IntegrationFilters';

export default function SAIntegrationsCenter() {
  const [activeFilter, setActiveFilter] = useState<IntegrationFilterValue>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const counts = useMemo(() => {
    return {
      all: saIntegrations.length,
      connected: saIntegrations.filter((i) => i.status === 'connected').length,
      disconnected: saIntegrations.filter((i) => i.status === 'disconnected').length,
      error: saIntegrations.filter((i) => i.status === 'error').length,
      pending: saIntegrations.filter((i) => i.status === 'pending').length,
    };
  }, []);

  const filtered = useMemo(() => {
    let list = saIntegrations;
    if (activeFilter !== 'all') {
      list = list.filter((i) => i.status === activeFilter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (i) =>
          i.name.toLowerCase().includes(q) ||
          i.category.toLowerCase().includes(q)
      );
    }
    return list;
  }, [activeFilter, searchQuery]);

  const today = useMemo(() => {
    return new Date().toLocaleDateString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  }, []);

  return (
    <div className="min-h-screen bg-background-100">
      <div className="flex">
        <SASidebar />
        <main className="flex-1 min-w-0">
          <div className="p-4 md:p-6 lg:p-8 pb-8 space-y-6 max-w-[1280px] mx-auto">

            {/* ── Page Header ── */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h1 className="text-foreground-950 font-bold text-lg md:text-xl">
                  <i className="ri-puzzle-2-line text-foreground-500 mr-1.5"></i>
                  Platform Integrations Center
                </h1>
                <p className="text-foreground-400 text-xs mt-0.5 capitalize">
                  {today} · {counts.connected}/{counts.all} integraciones conectadas
                </p>
              </div>
            </div>

            {/* ── Stats overview ── */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {[
                { key: 'all', label: 'Total', icon: 'ri-puzzle-2-line', accent: 'bg-primary-50 text-primary-600' },
                { key: 'connected', label: 'Conectadas', icon: 'ri-checkbox-circle-line', accent: 'bg-emerald-50 text-emerald-600' },
                { key: 'disconnected', label: 'Desconectadas', icon: 'ri-close-circle-line', accent: 'bg-red-50 text-red-500' },
                { key: 'error', label: 'Con errores', icon: 'ri-error-warning-line', accent: 'bg-amber-50 text-amber-600' },
                { key: 'pending', label: 'Pendientes', icon: 'ri-time-line', accent: 'bg-secondary-50 text-secondary-600' },
              ].map((stat) => (
                <button
                  key={stat.key}
                  onClick={() => setActiveFilter(stat.key as IntegrationFilterValue)}
                  className={`bg-background-50 border rounded-2xl p-4 text-left transition-colors cursor-pointer ${
                    activeFilter === stat.key ? 'border-primary-300' : 'border-background-200 hover:border-background-300/70'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-lg ${stat.accent} flex items-center justify-center mb-2`}>
                    <i className={`${stat.icon} text-sm`}></i>
                  </div>
                  <p className="text-foreground-950 text-xl font-extrabold tracking-tight leading-none">
                    {counts[stat.key as keyof typeof counts]}
                  </p>
                  <p className="text-foreground-400 text-[10px] mt-0.5">{stat.label}</p>
                </button>
              ))}
            </div>

            {/* ── Filters ── */}
            <IntegrationFilters
              activeFilter={activeFilter}
              onFilterChange={setActiveFilter}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              counts={counts}
            />

            {/* ── Integration list ── */}
            {filtered.length === 0 ? (
              <div className="flex flex-col items-center text-center gap-3 py-16">
                <div className="w-12 h-12 rounded-full bg-background-200 flex items-center justify-center">
                  <i className="ri-puzzle-line text-foreground-300 text-xl"></i>
                </div>
                <div>
                  <p className="text-foreground-800 text-sm font-semibold">Sin resultados</p>
                  <p className="text-foreground-400 text-xs mt-0.5">
                    {searchQuery ? 'Probá con otro término de búsqueda.' : 'No hay integraciones con ese filtro.'}
                  </p>
                </div>
                {(activeFilter !== 'all' || searchQuery) && (
                  <button
                    onClick={() => { setActiveFilter('all'); setSearchQuery(''); }}
                    className="px-4 py-2 rounded-lg bg-primary-50 text-primary-600 text-xs font-bold hover:bg-primary-100 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Limpiar filtros
                  </button>
                )}
              </div>
            ) : viewMode === 'grid' ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
                {filtered.map((integration, idx) => (
                  <IntegrationCard key={integration.id} integration={integration} index={idx} />
                ))}
              </div>
            ) : (
              <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden divide-y divide-background-200">
                {filtered.map((integration) => (
                  <IntegrationListItem key={integration.id} integration={integration} />
                ))}
              </div>
            )}

            {/* ── Legend ── */}
            <div className="flex flex-wrap items-center gap-4 text-[10px] text-foreground-400">
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-emerald-500"></span> Conectada
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-red-500"></span> Desconectada
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-amber-500"></span> Con errores
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-secondary-500"></span> Pendiente
              </span>
            </div>

          </div>
        </main>
      </div>

      {/* Mobile bottom bar */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-background-50/98 backdrop-blur-2xl border-t border-background-200 py-2.5 px-4 safe-bottom">
        <p className="text-center text-foreground-400 text-[10px] font-medium">
          Integrations Center · RadioFrecuenciaWeb
        </p>
      </div>
    </div>
  );
}